import type { AppConfig, TopicConfig, CategoryConfig, TopicCategory } from '../types';

export const APP_CONFIG: AppConfig = {
  maxGuestQuestions: 10,
  maxQuestionsPerSession: 100, // Hard limit for all sessions
  defaultQuestionsPerSession: 20,
  pointsPerCorrectAnswer: 10,
  pointsPerStreak: 5,
  masteryThreshold: 80,
  adaptiveLearning: {
    enabled: true,
    adjustmentThreshold: 70,
    minConsecutiveForIncrease: 3,
    minConsecutiveForDecrease: 2,
  },
};

export const CATEGORY_CONFIGS: Record<TopicCategory, CategoryConfig> = {
  arithmetic: {
    id: 'arithmetic',
    name: 'Arithmetic',
    icon: 'calculate',
    color: 'primary',
    gradeRange: 'K-3',
    description: 'Basic math operations',
    order: 1,
  },
  patterns: {
    id: 'patterns',
    name: 'Patterns & Sequences',
    icon: 'sequences',
    color: 'tertiary',
    gradeRange: 'K-3',
    description: 'Number patterns and counting',
    order: 2,
  },
  'time-money': {
    id: 'time-money',
    name: 'Time & Money',
    icon: 'time',
    color: 'success',
    gradeRange: '1-3',
    description: 'Clock reading and counting money',
    order: 3,
  },
  measurement: {
    id: 'measurement',
    name: 'Measurement',
    icon: 'ruler',
    color: 'warning',
    gradeRange: '1-5',
    description: 'Length, weight, and capacity',
    order: 4,
  },
  geometry: {
    id: 'geometry',
    name: 'Geometry',
    icon: 'geometry',
    color: 'error',
    gradeRange: 'K-6',
    description: 'Shapes, area, and angles',
    order: 5,
  },
  'number-theory': {
    id: 'number-theory',
    name: 'Number Theory',
    icon: 'numbers',
    color: 'secondary',
    gradeRange: '3-6',
    description: 'Factors, primes, and factorials',
    order: 6,
  },
  fractions: {
    id: 'fractions',
    name: 'Fractions & Decimals',
    icon: 'fraction',
    color: 'primary',
    gradeRange: '3-6',
    description: 'Fractions, decimals, and percentages',
    order: 7,
  },
  statistics: {
    id: 'statistics',
    name: 'Statistics & Probability',
    icon: 'stats',
    color: 'success',
    gradeRange: '3-8',
    description: 'Data analysis and probability',
    order: 8,
  },
  'pre-algebra': {
    id: 'pre-algebra',
    name: 'Pre-Algebra',
    icon: 'variable',
    color: 'secondary',
    gradeRange: '6-8',
    description: 'Variables, expressions, and equations',
    order: 9,
  },
  algebra: {
    id: 'algebra',
    name: 'Algebra',
    icon: 'function',
    color: 'tertiary',
    gradeRange: '8-10',
    description: 'Linear equations and graphing',
    order: 10,
  },
};

export const TOPIC_CONFIGS: Record<string, TopicConfig> = {
  // Individual arithmetic operations
  addition: {
    topicType: 'addition',
    name: 'Addition',
    shortName: 'Add',
    description: 'Practice adding numbers together',
    icon: 'addition',
    color: 'primary',
    ageGroups: ['4-5', '5-6', '6-7'],
    concepts: ['addition-under-10', 'addition-under-20', 'addition-under-50', 'addition-under-100'],
    maxDifficulty: 10,
    category: 'arithmetic',
    gradeRange: 'K-3',
    status: 'available',
  },
  subtraction: {
    topicType: 'subtraction',
    name: 'Subtraction',
    shortName: 'Sub',
    description: 'Practice taking away numbers',
    icon: 'subtraction',
    color: 'error',
    ageGroups: ['4-5', '5-6', '6-7'],
    concepts: [
      'subtraction-under-10',
      'subtraction-under-20',
      'subtraction-under-50',
      'subtraction-under-100',
    ],
    maxDifficulty: 10,
    category: 'arithmetic',
    gradeRange: 'K-3',
    status: 'available',
  },
  multiplication: {
    topicType: 'multiplication',
    name: 'Multiplication',
    shortName: 'Mult',
    description: 'Learn times tables',
    icon: 'multiplication',
    color: 'secondary',
    ageGroups: ['5-6', '6-7', '7-8'],
    concepts: [
      'multiplication-2',
      'multiplication-5',
      'multiplication-10',
      'multiplication-tables',
    ],
    maxDifficulty: 10,
    category: 'arithmetic',
    gradeRange: '2-4',
    status: 'available',
  },
  division: {
    topicType: 'division',
    name: 'Division',
    shortName: 'Div',
    description: 'Learn to divide numbers',
    icon: 'division',
    color: 'tertiary',
    ageGroups: ['5-6', '6-7', '7-8'],
    concepts: ['division-by-2', 'division-by-5', 'division-by-10', 'division-basic'],
    maxDifficulty: 10,
    category: 'arithmetic',
    gradeRange: '2-4',
    status: 'available',
  },
  factors: {
    topicType: 'factors',
    name: 'Factors',
    shortName: 'Fac',
    description: 'Find all factors of a number',
    icon: 'factors',
    color: 'success',
    ageGroups: ['6-7', '7-8', '8-9'],
    concepts: ['factors-small', 'factors-medium', 'factors-large'],
    maxDifficulty: 8,
    category: 'number-theory',
    gradeRange: '3-6',
    status: 'available',
  },
  primes: {
    topicType: 'primes',
    name: 'Prime Numbers',
    shortName: 'Prm',
    description: 'Identify prime numbers',
    icon: 'primes',
    color: 'warning',
    ageGroups: ['6-7', '7-8', '8-9'],
    concepts: ['primes-under-20', 'primes-under-50', 'primes-under-100'],
    maxDifficulty: 8,
    category: 'number-theory',
    gradeRange: '3-6',
    status: 'available',
  },
  factorials: {
    topicType: 'factorials',
    name: 'Factorials',
    shortName: 'Fact',
    description: 'Calculate factorials (n!)',
    icon: 'factorials',
    color: 'primary',
    ageGroups: ['7-8', '8-9', '9-10'],
    concepts: ['factorial-small', 'factorial-medium'],
    maxDifficulty: 6,
    category: 'number-theory',
    gradeRange: '4-6',
    status: 'available',
  },
  // Legacy arithmetic (all operations) - kept for backward compatibility
  arithmetic: {
    topicType: 'arithmetic',
    name: 'Mixed Arithmetic',
    shortName: 'Mixed',
    description: 'Addition, subtraction, multiplication, and division',
    icon: 'calculate',
    color: 'primary',
    ageGroups: ['4-5', '5-6', '6-7'],
    concepts: [
      'addition-under-10',
      'addition-under-20',
      'subtraction-under-10',
      'subtraction-under-20',
      'multiplication-tables',
      'division-basic',
    ],
    maxDifficulty: 10,
    category: 'arithmetic',
    gradeRange: 'K-3',
    status: 'available',
  },
  sequences: {
    topicType: 'sequences',
    name: 'Number Sequences',
    shortName: 'Seq',
    description: 'Patterns and missing numbers',
    icon: 'sequences',
    color: 'secondary',
    ageGroups: ['4-5', '5-6', '6-7'],
    concepts: [
      'counting-forward',
      'counting-backward',
      'skip-counting-2',
      'skip-counting-5',
      'skip-counting-10',
      'pattern-recognition',
    ],
    maxDifficulty: 10,
    category: 'patterns',
    gradeRange: 'K-3',
    status: 'available',
  },
  time: {
    topicType: 'time',
    name: 'Time & Clock Reading',
    shortName: 'Time',
    description: 'Learn to read analog and digital clocks',
    icon: 'time',
    color: 'success',
    ageGroups: ['5-6', '6-7'],
    concepts: [
      'hours-only',
      'half-hours',
      'quarter-hours',
      'minutes-5',
      'minutes-any',
      'digital-to-analog',
    ],
    maxDifficulty: 8,
    category: 'time-money',
    gradeRange: '1-3',
    status: 'available',
  },
  money: {
    topicType: 'money',
    name: 'Money Math',
    shortName: 'Cash',
    description: 'Counting coins and bills',
    icon: 'moneyTopic',
    color: 'warning',
    ageGroups: ['5-6', '6-7'],
    concepts: [
      'coin-recognition',
      'coin-values',
      'counting-pennies',
      'counting-nickels',
      'counting-dimes',
      'counting-quarters',
      'mixed-coins',
      'making-change',
    ],
    maxDifficulty: 8,
    category: 'time-money',
    gradeRange: '1-3',
    status: 'available',
  },

  // ========================================
  // MEASUREMENT TOPICS (Singaporean P1-P4)
  // ========================================
  length: {
    topicType: 'length',
    name: 'Length Measurement',
    shortName: 'Length',
    description: 'Measure and convert length (cm, m)',
    icon: 'length',
    color: 'primary',
    ageGroups: ['6-7', '7-8', '8-9'],
    concepts: [
      'length-comparison',
      'ruler-reading-cm',
      'cm-to-m',
      'm-to-cm',
      'mixed-to-single-cm',
      'single-to-mixed-m-cm',
      'length-word-problem-difference',
      'length-word-problem-division',
      'length-word-problem-addition',
    ],
    maxDifficulty: 10,
    category: 'measurement',
    gradeRange: 'P1-P4',
    status: 'available',
  },
  weight: {
    topicType: 'weight',
    name: 'Weight Measurement',
    shortName: 'Weight',
    description: 'Measure and convert weight (g, kg)',
    icon: 'weight',
    color: 'secondary',
    ageGroups: ['6-7', '7-8', '8-9'],
    concepts: [
      'weight-comparison',
      'scale-reading-kg',
      'scale-reading-g',
      'g-to-kg',
      'kg-to-g',
      'mixed-to-single-g',
      'single-to-mixed-kg-g',
      'weight-word-problem-difference',
      'weight-word-problem-division',
      'weight-word-problem-addition',
    ],
    maxDifficulty: 10,
    category: 'measurement',
    gradeRange: 'P1-P4',
    status: 'available',
  },
  capacity: {
    topicType: 'capacity',
    name: 'Capacity Measurement',
    shortName: 'Capacity',
    description: 'Measure and convert capacity (mL, L)',
    icon: 'capacity',
    color: 'tertiary',
    ageGroups: ['6-7', '7-8', '8-9'],
    concepts: [
      'capacity-comparison',
      'measurement-reading-L',
      'measurement-reading-mL',
      'mL-to-L',
      'L-to-mL',
      'mixed-to-single-mL',
      'single-to-mixed-L-mL',
      'capacity-word-problem-difference',
      'capacity-word-problem-division',
      'capacity-word-problem-addition',
    ],
    maxDifficulty: 10,
    category: 'measurement',
    gradeRange: 'P1-P4',
    status: 'available',
  },
  temperature: {
    topicType: 'temperature',
    name: 'Temperature',
    shortName: 'Temp',
    description: 'Read thermometers and convert °C/°F',
    icon: 'temperature',
    color: 'warning',
    ageGroups: ['8-9', '9-10'],
    concepts: [
      'thermometer-reading-C',
      'temperature-comparison',
      'temperature-difference',
      'C-to-F',
      'F-to-C',
      'temperature-word-problem-difference',
      'temperature-word-problem-increase',
      'temperature-word-problem-decrease',
    ],
    maxDifficulty: 10,
    category: 'measurement',
    gradeRange: 'P3-P4',
    status: 'available',
  },
  'unit-conversion': {
    topicType: 'unit-conversion',
    name: 'Unit Conversions',
    shortName: 'Convert',
    description: 'Convert between measurement units',
    icon: 'conversion',
    color: 'success',
    ageGroups: ['8-9', '9-10'],
    concepts: [
      'length-cm-to-m',
      'length-m-to-cm',
      'weight-g-to-kg',
      'weight-kg-to-g',
      'capacity-mL-to-L',
      'capacity-L-to-mL',
      'length-mixed-to-single',
      'length-single-to-mixed',
      'weight-mixed-to-single',
      'weight-single-to-mixed',
      'capacity-mixed-to-single',
      'capacity-single-to-mixed',
      'cross-category-length',
      'cross-category-capacity',
      'cross-category-weight',
    ],
    maxDifficulty: 10,
    category: 'measurement',
    gradeRange: 'P3-P4',
    status: 'available',
  },

  // ========================================
  // COMING SOON - Pre-Algebra
  // ========================================
  'negative-numbers': {
    topicType: 'negative-numbers',
    name: 'Negative Numbers',
    shortName: 'Neg',
    description: 'Work with numbers below zero',
    icon: 'subtraction',
    color: 'secondary',
    ageGroups: ['7-8', '8-9', '9-10'],
    concepts: [],
    maxDifficulty: 8,
    category: 'pre-algebra',
    gradeRange: '6-8',
    status: 'coming-soon',
  },
  exponents: {
    topicType: 'exponents',
    name: 'Exponents',
    shortName: 'Exp',
    description: 'Powers and exponential notation',
    icon: 'multiplication',
    color: 'secondary',
    ageGroups: ['7-8', '8-9', '9-10'],
    concepts: [],
    maxDifficulty: 8,
    category: 'pre-algebra',
    gradeRange: '6-8',
    status: 'coming-soon',
  },
  'square-roots': {
    topicType: 'square-roots',
    name: 'Square Roots',
    shortName: 'Sqrt',
    description: 'Find square roots of perfect squares',
    icon: 'calculate',
    color: 'secondary',
    ageGroups: ['7-8', '8-9', '9-10'],
    concepts: [],
    maxDifficulty: 8,
    category: 'pre-algebra',
    gradeRange: '6-8',
    status: 'coming-soon',
  },
  'order-of-operations': {
    topicType: 'order-of-operations',
    name: 'Order of Operations',
    shortName: 'PEMDAS',
    description: 'Practice PEMDAS rules',
    icon: 'calculate',
    color: 'primary',
    ageGroups: ['6-7', '7-8', '8-9'],
    concepts: [],
    maxDifficulty: 10,
    category: 'arithmetic',
    gradeRange: '4-6',
    status: 'coming-soon',
  },
  variables: {
    topicType: 'variables',
    name: 'Variables',
    shortName: 'Var',
    description: 'Solve for x in simple equations',
    icon: 'calculate',
    color: 'secondary',
    ageGroups: ['8-9', '9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'pre-algebra',
    gradeRange: '6-8',
    status: 'coming-soon',
  },
  expressions: {
    topicType: 'expressions',
    name: 'Expressions',
    shortName: 'Expr',
    description: 'Evaluate algebraic expressions',
    icon: 'calculate',
    color: 'secondary',
    ageGroups: ['8-9', '9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'pre-algebra',
    gradeRange: '6-8',
    status: 'coming-soon',
  },
  inequalities: {
    topicType: 'inequalities',
    name: 'Inequalities',
    shortName: 'Ineq',
    description: 'Compare with <, >, ≤, ≥',
    icon: 'calculate',
    color: 'secondary',
    ageGroups: ['8-9', '9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'pre-algebra',
    gradeRange: '6-8',
    status: 'coming-soon',
  },
  ratios: {
    topicType: 'ratios',
    name: 'Ratios & Proportions',
    shortName: 'Ratio',
    description: 'Work with ratios and proportions',
    icon: 'division',
    color: 'secondary',
    ageGroups: ['8-9', '9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'pre-algebra',
    gradeRange: '6-8',
    status: 'coming-soon',
  },

  // ========================================
  // COMING SOON - Fractions & Decimals
  // ========================================
  'fraction-basics': {
    topicType: 'fraction-basics',
    name: 'Fraction Basics',
    shortName: 'Frac',
    description: 'Identify and understand fractions',
    icon: 'division',
    color: 'primary',
    ageGroups: ['5-6', '6-7', '7-8'],
    concepts: [],
    maxDifficulty: 8,
    category: 'fractions',
    gradeRange: '3-5',
    status: 'coming-soon',
  },
  'equivalent-fractions': {
    topicType: 'equivalent-fractions',
    name: 'Equivalent Fractions',
    shortName: 'Equiv',
    description: 'Find equal fractions (1/2 = 2/4)',
    icon: 'division',
    color: 'primary',
    ageGroups: ['5-6', '6-7', '7-8'],
    concepts: [],
    maxDifficulty: 8,
    category: 'fractions',
    gradeRange: '3-5',
    status: 'coming-soon',
  },
  'fraction-addition': {
    topicType: 'fraction-addition',
    name: 'Fraction Addition',
    shortName: 'FracAdd',
    description: 'Add and subtract fractions',
    icon: 'addition',
    color: 'primary',
    ageGroups: ['6-7', '7-8', '8-9'],
    concepts: [],
    maxDifficulty: 10,
    category: 'fractions',
    gradeRange: '4-6',
    status: 'coming-soon',
  },
  decimals: {
    topicType: 'decimals',
    name: 'Decimals',
    shortName: 'Dec',
    description: 'Decimal place value and operations',
    icon: 'calculate',
    color: 'primary',
    ageGroups: ['6-7', '7-8', '8-9'],
    concepts: [],
    maxDifficulty: 10,
    category: 'fractions',
    gradeRange: '4-6',
    status: 'coming-soon',
  },
  'fraction-decimal': {
    topicType: 'fraction-decimal',
    name: 'Fraction ↔ Decimal',
    shortName: 'Conv',
    description: 'Convert between fractions and decimals',
    icon: 'calculate',
    color: 'primary',
    ageGroups: ['6-7', '7-8', '8-9'],
    concepts: [],
    maxDifficulty: 10,
    category: 'fractions',
    gradeRange: '4-6',
    status: 'coming-soon',
  },
  percentages: {
    topicType: 'percentages',
    name: 'Percentages',
    shortName: 'Pct',
    description: 'Work with percentages',
    icon: 'calculate',
    color: 'primary',
    ageGroups: ['7-8', '8-9', '9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'fractions',
    gradeRange: '5-7',
    status: 'coming-soon',
  },

  // ========================================
  // COMING SOON - Geometry
  // ========================================
  'shape-recognition': {
    topicType: 'shape-recognition',
    name: 'Shape Recognition',
    shortName: 'Shapes',
    description: 'Identify circles, squares, triangles',
    icon: 'geometry',
    color: 'error',
    ageGroups: ['4-5', '5-6', '6-7'],
    concepts: [],
    maxDifficulty: 6,
    category: 'geometry',
    gradeRange: 'K-2',
    status: 'coming-soon',
  },
  perimeter: {
    topicType: 'perimeter',
    name: 'Perimeter',
    shortName: 'Peri',
    description: 'Calculate perimeter of shapes',
    icon: 'geometry',
    color: 'error',
    ageGroups: ['5-6', '6-7', '7-8'],
    concepts: [],
    maxDifficulty: 8,
    category: 'geometry',
    gradeRange: '3-5',
    status: 'coming-soon',
  },
  area: {
    topicType: 'area',
    name: 'Area',
    shortName: 'Area',
    description: 'Calculate area of rectangles and triangles',
    icon: 'geometry',
    color: 'error',
    ageGroups: ['6-7', '7-8', '8-9'],
    concepts: [],
    maxDifficulty: 10,
    category: 'geometry',
    gradeRange: '4-6',
    status: 'coming-soon',
  },
  volume: {
    topicType: 'volume',
    name: 'Volume',
    shortName: 'Vol',
    description: 'Calculate volume of 3D shapes',
    icon: 'geometry',
    color: 'error',
    ageGroups: ['7-8', '8-9', '9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'geometry',
    gradeRange: '5-7',
    status: 'coming-soon',
  },
  angles: {
    topicType: 'angles',
    name: 'Angles',
    shortName: 'Ang',
    description: 'Identify acute, obtuse, and right angles',
    icon: 'geometry',
    color: 'error',
    ageGroups: ['6-7', '7-8', '8-9'],
    concepts: [],
    maxDifficulty: 8,
    category: 'geometry',
    gradeRange: '4-6',
    status: 'coming-soon',
  },
  'coordinate-plane': {
    topicType: 'coordinate-plane',
    name: 'Coordinate Plane',
    shortName: 'Coord',
    description: 'Plot points on (x, y) grid',
    icon: 'geometry',
    color: 'error',
    ageGroups: ['7-8', '8-9', '9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'geometry',
    gradeRange: '5-7',
    status: 'coming-soon',
  },

  // ========================================
  // COMING SOON - Statistics & Probability
  // ========================================
  'mean-median-mode': {
    topicType: 'mean-median-mode',
    name: 'Mean, Median, Mode',
    shortName: 'Avg',
    description: 'Calculate central tendency',
    icon: 'stats',
    color: 'success',
    ageGroups: ['8-9', '9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'statistics',
    gradeRange: '6-8',
    status: 'coming-soon',
  },
  probability: {
    topicType: 'probability',
    name: 'Probability',
    shortName: 'Prob',
    description: 'Coin flips, dice, and chance',
    icon: 'stats',
    color: 'success',
    ageGroups: ['8-9', '9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'statistics',
    gradeRange: '6-8',
    status: 'coming-soon',
  },
  combinations: {
    topicType: 'combinations',
    name: 'Combinations',
    shortName: 'nCr',
    description: 'Calculate nCr combinations',
    icon: 'factorials',
    color: 'success',
    ageGroups: ['9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'statistics',
    gradeRange: '9-10',
    status: 'coming-soon',
  },
  permutations: {
    topicType: 'permutations',
    name: 'Permutations',
    shortName: 'nPr',
    description: 'Calculate nPr permutations',
    icon: 'factorials',
    color: 'success',
    ageGroups: ['9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'statistics',
    gradeRange: '9-10',
    status: 'coming-soon',
  },
  'data-charts': {
    topicType: 'data-charts',
    name: 'Data & Charts',
    shortName: 'Charts',
    description: 'Read bar and pie charts',
    icon: 'stats',
    color: 'success',
    ageGroups: ['5-6', '6-7', '7-8'],
    concepts: [],
    maxDifficulty: 8,
    category: 'statistics',
    gradeRange: '3-5',
    status: 'coming-soon',
  },

  // ========================================
  // COMING SOON - Algebra
  // ========================================
  'linear-equations': {
    topicType: 'linear-equations',
    name: 'Linear Equations',
    shortName: 'Linear',
    description: 'Solve equations like 2x + 3 = 11',
    icon: 'calculate',
    color: 'tertiary',
    ageGroups: ['9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'algebra',
    gradeRange: '8-10',
    status: 'coming-soon',
  },
  quadratics: {
    topicType: 'quadratics',
    name: 'Quadratics',
    shortName: 'Quad',
    description: 'Solve quadratic equations',
    icon: 'calculate',
    color: 'tertiary',
    ageGroups: ['9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'algebra',
    gradeRange: '9-10',
    status: 'coming-soon',
  },
  graphing: {
    topicType: 'graphing',
    name: 'Graphing',
    shortName: 'Graph',
    description: 'Graph y = mx + b',
    icon: 'geometry',
    color: 'tertiary',
    ageGroups: ['9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'algebra',
    gradeRange: '8-10',
    status: 'coming-soon',
  },
  systems: {
    topicType: 'systems',
    name: 'Systems of Equations',
    shortName: 'Sys',
    description: 'Solve two equations',
    icon: 'calculate',
    color: 'tertiary',
    ageGroups: ['9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'algebra',
    gradeRange: '9-10',
    status: 'coming-soon',
  },
  polynomials: {
    topicType: 'polynomials',
    name: 'Polynomials',
    shortName: 'Poly',
    description: 'Multiply polynomials like (x+2)(x-3)',
    icon: 'calculate',
    color: 'tertiary',
    ageGroups: ['9-10'],
    concepts: [],
    maxDifficulty: 10,
    category: 'algebra',
    gradeRange: '9-10',
    status: 'coming-soon',
  },
};

export const AVATARS = [
  '🦁',
  '🐯',
  '🐻',
  '🐼',
  '🐨',
  '🐸',
  '🐰',
  '🦊',
  '🐱',
  '🐶',
  '🐵',
  '🦄',
  '🐲',
  '🦖',
  '🐢',
  '🐙',
];
